/**
 * TaskFirestoreService
 *
 * Firestore CRUD + real-time subscriptions for backend tasks.
 * Collection path: projects/{projectId}/backend_tasks
 *
 * This service handles:
 * - Single task CRUD (create, get, update, complete, fail)
 * - Collection-level subscription (all project tasks, for Zustand store)
 * - Single task subscription (for individual task tracking)
 * - Mark task as viewed (arrayUnion for viewedBy)
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  arrayUnion,
  Unsubscribe,
  DocumentSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { backendTaskConverter } from './backendTaskConverter';
import type { BackendTask, TaskStatus } from '../types';

export const API_ONLY_BACKGROUND_TASK_ACTION = 'ad-creative-video-transcription';

export interface CreateTaskInput {
  action: string;
  description?: string;
  userId: string;
  projectId: string;
  strategyId?: string;
  blockId?: string;
  taskType?: string;
  provider?: string;
  model?: string;
  targetEntityPath?: BackendTask['targetEntityPath'];
  phases?: BackendTask['phases'];
  currentPhaseKey?: string;
}

export interface UpdateTaskInput {
  status?: TaskStatus;
  result?: Record<string, unknown>;
  error?: string;
  cancelReason?: string;
  usage?: BackendTask['usage'];
  phases?: BackendTask['phases'];
  currentPhaseKey?: string;
}

/** A single document-level change from a collection snapshot. */
export interface TaskCollectionChange {
  type: 'added' | 'modified' | 'removed';
  task: BackendTask;
}

export class TaskFirestoreService {
  private readonly collectionName = 'backend_tasks';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  private getCollectionPath(projectId: string): string {
    return `projects/${projectId}/${this.collectionName}`;
  }

  /**
   * Firestore rules cannot redact one document from an otherwise broad list.
   * Keep the global task surface on the exact query proven by the list rule;
   * automatic video-transcription progress is read through its bounded API.
   */
  private getClientVisibleCollectionQuery(projectId: string) {
    const collectionRef = collection(
      this.firestore,
      this.getCollectionPath(projectId)
    ).withConverter(backendTaskConverter);
    return query(
      collectionRef,
      // `action` is required on every BackendTask; `taskType` is optional on
      // older ordinary tasks and a `!=` query would silently omit them.
      where('action', '!=', API_ONLY_BACKGROUND_TASK_ACTION)
    );
  }

  /**
   * Create a new task with 'running' status
   */
  async create(input: CreateTaskInput): Promise<BackendTask> {
    const collectionPath = this.getCollectionPath(input.projectId);
    const collectionRef = collection(this.firestore, collectionPath);
    const docRef = doc(collectionRef).withConverter(backendTaskConverter);

    const task: BackendTask = {
      id: docRef.id,
      status: 'running',
      action: input.action,
      description: input.description,
      userId: input.userId,
      projectId: input.projectId,
      strategyId: input.strategyId,
      blockId: input.blockId,
      taskType: input.taskType as BackendTask['taskType'],
      provider: input.provider,
      model: input.model,
      targetEntityPath: input.targetEntityPath,
      phases: input.phases,
      currentPhaseKey: input.currentPhaseKey,
      viewedBy: [],
      createdAt: new Date(),
    };

    await setDoc(docRef, task);
    return task;
  }

  /**
   * Get a task by ID
   */
  async get(projectId: string, taskId: string): Promise<BackendTask | null> {
    const docRef = doc(
      this.firestore,
      this.getCollectionPath(projectId),
      taskId
    ).withConverter(backendTaskConverter);

    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  }

  /**
   * Update task fields (uses updateDoc with dot-notation safe merge)
   */
  async update(
    projectId: string,
    taskId: string,
    data: UpdateTaskInput
  ): Promise<void> {
    const docRef = doc(
      this.firestore,
      this.getCollectionPath(projectId),
      taskId
    );

    const updateData: Record<string, unknown> = {
      ...data,
      updatedAt: new Date(),
    };

    await updateDoc(docRef, updateData);
  }

  /**
   * Mark task as completed with result
   */
  async complete(
    projectId: string,
    taskId: string,
    result: Record<string, unknown>
  ): Promise<void> {
    await this.update(projectId, taskId, { status: 'completed', result });
  }

  /**
   * Mark task as failed with error message
   */
  async fail(projectId: string, taskId: string, error: string): Promise<void> {
    await this.update(projectId, taskId, { status: 'error', error });
  }

  /**
   * Mark task as viewed by a user (atomic arrayUnion)
   */
  async markViewed(
    projectId: string,
    taskId: string,
    userId: string
  ): Promise<void> {
    const docRef = doc(
      this.firestore,
      this.getCollectionPath(projectId),
      taskId
    );

    await updateDoc(docRef, {
      viewedBy: arrayUnion(userId),
    });
  }

  /**
   * Subscribe to ALL tasks in a project (collection-level listener).
   * Used by the Zustand store to maintain real-time state.
   */
  subscribeToCollection(
    projectId: string,
    onNext: (tasks: BackendTask[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    const collectionQuery = this.getClientVisibleCollectionQuery(projectId);

    return onSnapshot(
      collectionQuery,
      (snapshot: QuerySnapshot<BackendTask>) => {
        const tasks = snapshot.docs.map((d) => d.data());
        onNext(tasks);
      },
      (error) => {
        CoreFirebaseService.getInstance().logError(
          error,
          'TaskFirestoreService.subscribeToCollection'
        );
        onError(error as Error);
      }
    );
  }

  /**
   * Subscribe to per-document CHANGES in a project's task collection.
   *
   * Unlike subscribeToCollection (which re-delivers the full doc list on every
   * snapshot), this delivers only snapshot.docChanges() — the store can patch
   * changed ids and keep every untouched task object referentially stable.
   * Field-compare heuristics (updatedAt/status) are NOT sufficient for that:
   * markViewed() writes viewedBy via arrayUnion without bumping updatedAt.
   */
  subscribeToCollectionChanges(
    projectId: string,
    onChanges: (changes: TaskCollectionChange[]) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    const collectionQuery = this.getClientVisibleCollectionQuery(projectId);

    return onSnapshot(
      collectionQuery,
      (snapshot: QuerySnapshot<BackendTask>) => {
        const changes: TaskCollectionChange[] = snapshot
          .docChanges()
          .map((change) => ({ type: change.type, task: change.doc.data() }));
        if (changes.length > 0) {
          onChanges(changes);
        }
      },
      (error) => {
        CoreFirebaseService.getInstance().logError(
          error,
          'TaskFirestoreService.subscribeToCollectionChanges'
        );
        onError(error as Error);
      }
    );
  }

  /**
   * Subscribe to a single task document
   */
  subscribe(
    projectId: string,
    taskId: string,
    onNext: (task: BackendTask | null) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    const docRef = doc(
      this.firestore,
      this.getCollectionPath(projectId),
      taskId
    ).withConverter(backendTaskConverter);

    return onSnapshot(
      docRef,
      (snapshot: DocumentSnapshot<BackendTask>) => {
        onNext(snapshot.exists() ? snapshot.data() : null);
      },
      (error) => {
        CoreFirebaseService.getInstance().logError(
          error,
          'TaskFirestoreService.subscribe'
        );
        onError(error as Error);
      }
    );
  }
}

export default TaskFirestoreService;
